import { Input } from '@components/Input';
import { Autocomplete, Box, createFilterOptions, debounce, Stack, SxProps, Typography } from '@mui/material';
import { Controller, get } from 'react-hook-form';
import { ReactComponent as PopupIcon } from '@icons/popupIcon.svg';
import { ReactComponent as CrossIcon } from '@icons/cross.svg';
import { ReactComponent as SearchIcon } from '@icons/search.svg';
import { ReactComponent as CheckIcon } from '@icons/System/check.svg';
import { Checkbox } from '@components/Checkbox';
import { FC, useCallback, useEffect, useState } from 'react';
import { PreloaderBubbles } from '@components/PreloaderBubbles';
import { buildDataTestIdAttribute, buildDataTestIdFromNestedName } from '@src/utils';

type FormAutocompleteFieldProps = {
  name: string;
  control: any;
  placeholder: string;
  options: any[];
  multiple?: boolean;
  maxLenght?: number;
  disabled?: boolean;
  onSearch?: (_e: React.SyntheticEvent, value: string, reason: string) => void;
  loading?: boolean;
  editValue?: any;
  withSearchIcon?: boolean;
  onClear?: () => void;
  onChange?: () => void;
  isEnd?: boolean;
  setValuesFilter?: (val: any) => void;
  onOpen?: () => void;
  withoutLocalValue?: boolean;
  defaultValue?: Nullable<string>;
  dataTestId?: string;
  disableClearIcon?: boolean;
  sx?: SxProps;
  clearable?: boolean;
};
export const FormAutocompleteField: FC<FormAutocompleteFieldProps> = ({
  name,
  control,
  placeholder,
  options,
  multiple = false,
  disabled = false,
  maxLenght,
  onSearch,
  loading,
  editValue,
  withSearchIcon,
  onClear,
  onChange,
  isEnd,
  setValuesFilter,
  onOpen,
  withoutLocalValue = false,
  defaultValue,
  dataTestId,
  disableClearIcon,
  clearable = true,
  sx,
}) => {
  const [localValue, setLocalValue] = useState<any>(null);
  const scrollRestSelect = useCallback(() => {
    if (isEnd) return;
    setValuesFilter &&
      setValuesFilter(prevState => {
        return {
          ...prevState,
          pageNumber: +prevState.pageNumber + 1,
        };
      });
  }, [isEnd, setValuesFilter]);

  const scrollRestSelectDebounce = debounce(scrollRestSelect, 100);

  const handleOnScrollSelect = (event: React.SyntheticEvent) => {
    if (isEnd && loading) {
      return;
    }
    const listboxNode = event.currentTarget;
    if (
      Math.ceil(listboxNode.scrollTop + listboxNode.clientHeight) > listboxNode.scrollHeight * 0.99 &&
      Math.ceil(listboxNode.scrollTop + listboxNode.clientHeight) <= listboxNode.scrollHeight * 1.01
    ) {
      scrollRestSelectDebounce();
    }
  };

  useEffect(() => {
    if (editValue) {
      setLocalValue(prev => prev || editValue);
    } else setLocalValue('');
  }, [editValue]);

  const dataTestIdStr = dataTestId || buildDataTestIdFromNestedName(name);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={'' || defaultValue}
      render={({ field: { ref, value, onChange: onChangeLocal, ...field }, formState }) => {
        const errorField = get(formState.errors, name);
        const error = errorField ? (errorField.message as string) : '';

        const selectedValue = multiple
          ? options?.filter(v => value.includes(v.value))
          : options?.find(op => op.value === value) || '';

        const valueAutocomplete = (withoutLocalValue ? '' : localValue) || selectedValue || null;

        return (
          <Stack flexDirection="column-reverse" sx={sx}>
            <Autocomplete
              options={options}
              sx={{ width: '100%' }}
              getOptionLabel={opt => (opt ? `${opt?.code || ''} ${opt?.name}` : '')}
              disablePortal
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              disableCloseOnSelect={multiple}
              disableClearable={disableClearIcon ?? false}
              popupIcon={<PopupIcon />}
              filterOptions={onSearch ? opts => opts : createFilterOptions()}
              multiple={multiple}
              ListboxProps={{ onScroll: handleOnScrollSelect }}
              limitTags={1}
              loadingText={
                <Box padding="12px">
                  <PreloaderBubbles size={16} />
                </Box>
              }
              noOptionsText="There are no results matching your search. Try to change your search options"
              onOpen={onOpen}
              disabled={disabled}
              autoComplete={!onSearch}
              value={valueAutocomplete}
              clearIcon={value && clearable ? <CrossIcon /> : null}
              loading={loading}
              onInputChange={onSearch}
              onChange={(_e, val, reason) => {
                if (reason === 'clear') {
                  onChangeLocal(multiple ? [] : null);
                  onClear && onClear();
                  onSearch && setLocalValue(null);
                } else {
                  onSearch && setLocalValue(val);
                  onChangeLocal(multiple ? val.map(v => v.value) : val.value);
                  onChange && onChange();
                }
              }}
              renderTags={tagValue => {
                return tagValue.map((option, index) => {
                  if (index === 0) {
                    return (
                      <Typography
                        key={option.value}
                        maxWidth="90%"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        overflow="hidden"
                        fontSize={14}
                        marginRight="4px"
                      >
                        {option.name}
                      </Typography>
                    );
                  }
                  return null;
                });
              }}
              renderInput={params => {
                return (
                  <Input
                    variant="outlined"
                    error={!!error}
                    placeholder={value?.length > 0 && placeholder ? '' : placeholder}
                    {...params}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: withSearchIcon ? (
                        <SearchIcon style={{ marginRight: '9px' }} />
                      ) : (
                        params.InputProps.startAdornment
                      ),
                      inputProps: {
                        ...params.inputProps,
                        maxLength: maxLenght,
                        ...buildDataTestIdAttribute(dataTestIdStr),
                      },
                    }}
                  />
                );
              }}
              renderOption={(props, option, { selected, index }) => {
                return (
                  <li
                    {...props}
                    key={option[option.key ? 'key' : 'value']}
                    data-name={name}
                    style={{
                      alignItems: 'start',
                      justifyContent: 'space-between',
                      background: selected ? '#FAFAFA' : 'transparent',
                    }}
                    {...buildDataTestIdAttribute(dataTestIdStr, `row-${index}`)}
                  >
                    <Stack direction="row" gap="10px" alignItems="center">
                      {multiple && <Checkbox style={{ marginRight: 8 }} checked={selected} color="secondary" />}
                      <Box>
                        {option.code && (
                          <Typography color="grey.700" variant="label14Bold" sx={{ flexShrink: 0, mr: '6px' }}>
                            {option.code}
                          </Typography>
                        )}
                        {option.name}
                      </Box>
                    </Stack>

                    {selected && <CheckIcon />}
                  </li>
                );
              }}
              {...field}
            />
          </Stack>
        );
      }}
    />
  );
};
